"use client";

import React, { Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Header from "@/components/common/Header";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

interface TeamMemberData {
  name: string;
  title: string;
  image: string;
  education: string[];
  barAdmissions: string[];
  yearsExperience: number;
  practiceAreas: string[];
  biography: string[];
  achievements: string[];
  professionalMemberships: string[];
  communityInvolvement: string[];
  philosophy: string;
}

const teamMembersData: Record<string, TeamMemberData> = {
  "zachary-lubbert": {
    name: "Zachary Lubbert",
    title: "Managing Partner & Senior Attorney",
    image: "/assets/images/unnamed-1765300959486.jpg",
    education: [
      "Juris Doctor, Harvard Law School (2007)",
      "Bachelor of Arts in Political Science, Yale University (2004)",
      "Graduate Certificate in Estate Planning, Northwestern University (2009)"
    ],
    barAdmissions: [
      "Illinois State Bar (2007)",
      "U.S. District Court, Northern District of Illinois (2008)",
      "U.S. Court of Appeals, Seventh Circuit (2010)"
    ],
    yearsExperience: 17,
    practiceAreas: [
      "Probate & Estate Litigation",
      "Estate Planning & Administration",
      "Trust Litigation",
      "Will Contests",
      "Elder Law"
    ],
    biography: [
      "Zachary Lubbert founded Lubbert Associates LLC with a vision to provide exceptional legal representation in estate and probate matters. With over 17 years of experience, he has successfully represented hundreds of clients in complex estate litigation cases.",
      "Before establishing the firm, Zachary served as a senior associate at a prestigious Chicago law firm, where he honed his expertise in high-stakes probate disputes. His strategic approach and attention to detail have earned him recognition among peers and clients alike.",
      "Zachary's practice focuses on helping families navigate the often complex and emotional landscape of estate administration, trust disputes, and will contests. He is known for his compassionate yet assertive representation, always prioritizing his clients' best interests."
    ],
    achievements: [
      "Super Lawyers Rising Star (2015-2019)",
      "Illinois State Bar Association Distinguished Service Award (2018)",
      "Successfully resolved over 200 estate litigation cases with favorable outcomes",
      "Published author in Illinois Bar Journal on estate planning strategies",
      "Frequent speaker at legal conferences on probate law"
    ],
    professionalMemberships: [
      "American Bar Association - Real Property, Trust and Estate Law Section",
      "Illinois State Bar Association - Trust and Estates Section Council",
      "Chicago Bar Association - Probate and Trust Law Committee",
      "National Academy of Elder Law Attorneys (NAELA)"
    ],
    communityInvolvement: [
      "Pro Bono Legal Services for low-income seniors",
      "Board Member, Chicago Elder Care Foundation",
      "Volunteer Estate Planning Clinics for veterans",
      "Guest Lecturer at DePaul University College of Law"
    ],
    philosophy: "I believe that every client deserves not just expert legal representation, but a trusted advisor who understands the personal nature of estate matters. My approach combines legal excellence with genuine empathy, ensuring clients feel supported throughout the entire legal process."
  },
  "sarah-mitchell": {
    name: "Sarah Mitchell",
    title: "Senior Associate Attorney - Personal Injury Law",
    image: "/assets/images/testimonial_sarah_martinez_portrait.png",
    education: [
      "Juris Doctor, University of Chicago Law School (2012)",
      "Bachelor of Science in Biomedical Engineering, Northwestern University (2009)",
      "Certificate in Trial Advocacy, National Institute for Trial Advocacy (2013)"
    ],
    barAdmissions: [
      "Illinois State Bar (2012)",
      "Wisconsin State Bar (2013)",
      "U.S. District Court, Northern District of Illinois (2013)"
    ],
    yearsExperience: 12,
    practiceAreas: [
      "Personal Injury Law",
      "Motor Vehicle Accidents",
      "Premises Liability",
      "Medical Malpractice",
      "Wrongful Death"
    ],
    biography: [
      "Sarah Mitchell brings a unique combination of legal expertise and technical knowledge to personal injury cases. Her background in biomedical engineering provides invaluable insight when handling complex medical malpractice and catastrophic injury cases.",
      "Since joining Lubbert Associates LLC in 2014, Sarah has secured millions of dollars in settlements and verdicts for injured clients. Her meticulous case preparation and persuasive courtroom presence have made her a formidable advocate for accident victims.",
      "Sarah is passionate about holding negligent parties accountable and ensuring her clients receive the compensation they deserve for their injuries, medical expenses, and pain and suffering."
    ],
    achievements: [
      "Million Dollar Advocates Forum Member",
      "Leading Lawyers Network - Personal Injury (2018-2024)",
      "Chicago Lawyer Magazine 40 Under 40 (2020)",
      "Secured $4.2M verdict in medical malpractice case (2021)",
      "AVVO Clients' Choice Award (2019, 2021, 2023)"
    ],
    professionalMemberships: [
      "American Association for Justice",
      "Illinois Trial Lawyers Association",
      "Chicago Bar Association - Tort Litigation Committee",
      "Women's Bar Association of Illinois"
    ],
    communityInvolvement: [
      "Volunteer Attorney, Legal Aid Chicago",
      "Board Member, Safe Roads Illinois Foundation",
      "Mentor, Women in Law Leadership Program",
      "Annual Safety Awareness Workshops for local schools"
    ],
    philosophy: "Personal injury cases are about more than legal claims—they're about people whose lives have been forever changed. I fight tirelessly to ensure my clients receive justice and the resources they need to rebuild their lives."
  },
  "robert-chen": {
    name: "Robert Chen",
    title: "Associate Attorney - Insurance & Business Law",
    image: "/assets/images/testimonial_robert_anderson_headshot.png",
    education: [
      "Juris Doctor, Northwestern University Pritzker School of Law (2014)",
      "Master of Business Administration, Kellogg School of Management (2014)",
      "Bachelor of Arts in Economics, University of Illinois (2010)"
    ],
    barAdmissions: [
      "Illinois State Bar (2014)",
      "Indiana State Bar (2015)",
      "U.S. District Court, Northern District of Illinois (2015)"
    ],
    yearsExperience: 10,
    practiceAreas: [
      "Insurance Law",
      "Business Contracts & Agreements",
      "Commercial Litigation",
      "Insurance Coverage Disputes",
      "Bad Faith Claims"
    ],
    biography: [
      "Robert Chen's dual expertise in law and business provides clients with a comprehensive approach to insurance and commercial matters. His MBA combined with his legal training allows him to understand the business implications of legal decisions.",
      "Robert has successfully represented both policyholders and businesses in complex insurance coverage disputes, contract negotiations, and commercial litigation. His strategic thinking and negotiation skills have resulted in favorable outcomes for numerous clients.",
      "Known for his analytical approach and clear communication, Robert excels at translating complex legal and business concepts into actionable strategies for his clients."
    ],
    achievements: [
      "Illinois Super Lawyers Rising Star (2019-2024)",
      "Best Lawyers: Ones to Watch - Insurance Law (2021-2024)",
      "Successfully negotiated over $10M in insurance settlements",
      "Featured speaker at Illinois State Bar Association Business Law Conference",
      "Published articles on insurance law in Northwestern Business Law Journal"
    ],
    professionalMemberships: [
      "American Bar Association - Business Law Section",
      "Illinois State Bar Association - Commercial Banking & UCC Committee",
      "Chicago Bar Association - Insurance Law Committee",
      "Defense Research Institute (DRI)"
    ],
    communityInvolvement: [
      "Small Business Legal Clinic volunteer",
      "Mentor, Northwestern Law Entrepreneur Law Clinic",
      "Board Member, Chicago Asian American Business Alliance",
      "Pro bono services for minority-owned businesses"
    ],
    philosophy: "Effective legal representation in business and insurance matters requires more than just knowing the law—it demands understanding the business context and crafting solutions that advance our clients' strategic objectives while protecting their legal rights."
  },
  "jennifer-martinez": {
    name: "Jennifer Martinez",
    title: "Associate Attorney - Estate Planning & Probate",
    image: "/assets/images/testimonial_jennifer_williams_portrait.png",
    education: [
      "Juris Doctor, Loyola University Chicago School of Law (2016)",
      "Bachelor of Arts in Psychology, DePaul University (2013)",
      "Certificate in Elder Law, National Elder Law Foundation (2018)"
    ],
    barAdmissions: [
      "Illinois State Bar (2016)",
      "U.S. District Court, Northern District of Illinois (2017)"
    ],
    yearsExperience: 8,
    practiceAreas: [
      "Estate Planning",
      "Probate Administration",
      "Trust Administration",
      "Guardianship Proceedings",
      "Special Needs Planning"
    ],
    biography: [
      "Jennifer Martinez joined Lubbert Associates LLC with a passion for helping families plan for their future and navigate the probate process with compassion and expertise. Her background in psychology provides unique insight into the emotional aspects of estate planning.",
      "Jennifer specializes in creating comprehensive estate plans that protect clients' assets and ensure their wishes are carried out. She is particularly skilled at working with families dealing with special needs planning and guardianship matters.",
      "Her empathetic approach and attention to detail have earned her a reputation for providing personalized legal services that address both the legal and emotional needs of her clients and their families."
    ],
    achievements: [
      "40 Under 40 Illinois Attorneys to Watch (2022)",
      "Leading Lawyers Emerging Lawyer - Estate Planning (2021-2024)",
      "Completed over 500 successful estate plans",
      "Guest columnist, Illinois Estate Planning Council Newsletter",
      "Community Service Award, Loyola Law Alumni Association (2023)"
    ],
    professionalMemberships: [
      "Illinois State Bar Association - Estate Planning Section",
      "Chicago Bar Association - Probate and Trust Law Committee",
      "National Association of Estate Planners & Councils",
      "Special Needs Alliance"
    ],
    communityInvolvement: [
      "Free Estate Planning Workshops for seniors",
      "Volunteer Attorney, Wills for Heroes Program",
      "Board Member, Special Needs Advocacy Foundation",
      "Guest speaker at community centers on estate planning basics"
    ],
    philosophy: "Estate planning is about more than documents—it's about peace of mind. I work closely with each client to understand their unique family dynamics and create plans that provide security and clarity for generations to come."
  }
};

function TeamMemberProfileContent() {
  const searchParams = useSearchParams();
  const memberSlug = searchParams?.get("member") || "zachary-lubbert";
  const member = teamMembersData[memberSlug] || teamMembersData["zachary-lubbert"];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Link 
              href="/about-us" 
              className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Our Team
            </Link>
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{member.name}</h1>
                <p className="text-xl text-blue-100 mb-6">{member.title}</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {member.yearsExperience} Years Experience
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {member.barAdmissions.length} Bar Admissions
                  </div>
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="w-64 h-64 rounded-lg overflow-hidden shadow-2xl border-4 border-white">
                  <AppImage
                    src={member.image}
                    alt={`${member.name} professional headshot`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-12">
                {/* Biography */}
                <div className="bg-white rounded-lg shadow-md p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Professional Biography</h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    {member.biography.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>

                {/* Philosophy */}
                <div className="bg-blue-50 rounded-lg shadow-md p-8 border-l-4 border-blue-900">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Philosophy</h2>
                  <p className="text-gray-700 italic leading-relaxed">&ldquo;{member.philosophy}&rdquo;</p>
                </div>

                {/* Achievements */}
                <div className="bg-white rounded-lg shadow-md p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Notable Achievements & Recognition</h2>
                  <ul className="space-y-3">
                    {member.achievements.map((achievement, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 text-blue-900 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Community Involvement */}
                <div className="bg-white rounded-lg shadow-md p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Community Involvement</h2>
                  <ul className="space-y-3">
                    {member.communityInvolvement.map((involvement, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 text-blue-900 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        <span className="text-gray-700">{involvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* Practice Areas */}
                <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Practice Areas</h3>
                  <ul className="space-y-2">
                    {member.practiceAreas.map((area, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-blue-900 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 text-sm">{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Education */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Education</h3>
                  <ul className="space-y-3">
                    {member.education.map((edu, index) => (
                      <li key={index} className="text-gray-700 text-sm border-l-2 border-blue-900 pl-3">
                        {edu}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bar Admissions */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Bar Admissions</h3>
                  <ul className="space-y-2">
                    {member.barAdmissions.map((bar, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-blue-900 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 text-sm">{bar}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Professional Memberships */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Professional Memberships</h3>
                  <ul className="space-y-2">
                    {member.professionalMemberships.map((membership, index) => (
                      <li key={index} className="text-gray-700 text-sm flex items-start">
                        <span className="text-blue-900 mr-2">•</span>
                        {membership}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="bg-blue-900 text-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold mb-3">Schedule a Consultation</h3>
                  <p className="text-blue-100 mb-4 text-sm">
                    Ready to discuss your legal matter with {member.name.split(' ')[0]}?
                  </p>
                  <Link
                    href="/contact"
                    className="block w-full bg-white text-blue-900 text-center py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Contact Us Today
                  </Link>
                  <p className="text-blue-200 text-xs mt-3 text-center">
                    +1-807-808-4167
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">©2019 Lubbert Associates LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default function TeamMemberProfile() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    }>
      <TeamMemberProfileContent />
    </Suspense>
  );
}